"use strict";
import { uniswapUrls } from "constants/urls";
import { FetchError } from "data/apiClients/FetchError";
import { TransactionStepType } from "features/transactions/steps/types";
import { isInterface } from "utilities/src/platform";
export class TransactionError extends Error {
}
export class UnexpectedTransactionStateError extends TransactionError {
  constructor(message) {
    super(message);
    this.name = "UnexpectedTransactionStateError";
  }
}
export class TransactionStepFailedError extends TransactionError {
  step;
  isBackendRejection;
  originalError;
  originalErrorStringified;
  originalErrorString;
  // originalErrorStringified error may get cut off by size limits; this acts as minimal backup
  stepStringified;
  constructor({
    message,
    step,
    isBackendRejection = false,
    originalError
  }) {
    super(message);
    this.name = "TransactionStepFailedError";
    this.step = step;
    this.isBackendRejection = isBackendRejection;
    this.originalError = originalError;
    try {
      this.originalErrorString = originalError?.toString();
      this.originalErrorStringified = JSON.stringify(originalError, null, 2);
      this.stepStringified = JSON.stringify(step, null, 2);
    } catch {
    }
  }
  getFingerprint() {
    const fingerprint = [this.step.type];
    try {
      if (this.originalError && "code" in this.originalError && (typeof this.originalError.code === "string" || typeof this.originalError.code === "number")) {
        fingerprint.push(String(this.originalError?.code));
      }
      if (this.originalError?.message) {
        fingerprint.push(String(this.originalError?.message));
      }
      if (this.isBackendRejection && this.originalError instanceof FetchError && this.originalError.data?.detail) {
        fingerprint.push(String(this.originalError.data.detail));
      }
    } catch (e) {
      if (isInterface) {
      }
    }
    return fingerprint;
  }
}
export class ApprovalEditedInWalletError extends TransactionStepFailedError {
  logError = false;
  constructor({ step }) {
    super({ message: "Approval decreased to insufficient amount in wallet", step });
  }
}
export class HandledTransactionInterrupt extends TransactionError {
  constructor(message) {
    super(message);
    this.name = "HandledTransactionInterrupt";
  }
}
export class StaleQuoteError extends TransactionError {
  constructor() {
    super("Could not confirm the latest price before submitting.");
    this.name = "StaleQuoteError";
  }
}
export function getErrorContent(t, error) {
  if (error instanceof StaleQuoteError) {
    return {
      title: t("swap.warning.quoteExpired.title"),
      message: t("swap.warning.quoteExpired.message")
    };
  }
  if (error instanceof TransactionStepFailedError) {
    return getStepSpecificErrorContent(t, error);
  }
  return {
    title: t("common.unknownError.error"),
    message: t("common.swap.failed")
  };
}
function getStepSpecificErrorContent(t, error) {
  switch (error.step.type) {
    case TransactionStepType.WrapTransaction:
      return {
        title: t("common.wrap.failed"),
        message: t("token.wrap.fail.message"),
        supportArticleURL: uniswapUrls.helpArticleUrls.wethExplainer
      };
    case TransactionStepType.SwapTransaction:
    case TransactionStepType.SwapTransactionAsync:
      return {
        title: t("common.swap.failed"),
        message: t("swap.fail.message"),
        supportArticleURL: uniswapUrls.helpArticleUrls.transactionFailure
      };
    case TransactionStepType.SwapTransactionBatched:
      return {
        title: t("swap.fail.batched.title"),
        buttonText: t("swap.fail.batched.retry"),
        message: t("swap.fail.batched"),
        supportArticleURL: uniswapUrls.helpArticleUrls.transactionFailure
      };
    case TransactionStepType.UniswapXSignature:
      if (error.isBackendRejection) {
        return {
          title: t("common.swap.failed"),
          message: t("swap.fail.uniswapX"),
          supportArticleURL: uniswapUrls.helpArticleUrls.uniswapXFailure
        };
      }
      return {
        title: t("common.swap.failed"),
        message: t("swap.fail.message"),
        supportArticleURL: uniswapUrls.helpArticleUrls.uniswapXFailure
      };
    case TransactionStepType.Permit2Signature:
      return {
        title: t("permit.approval.fail"),
        message: t("permit.approval.fail.message"),
        supportArticleURL: uniswapUrls.helpArticleUrls.approvalsExplainer
      };
    case TransactionStepType.TokenApprovalTransaction:
      if (error instanceof ApprovalEditedInWalletError) {
        return {
          title: t("error.tokenApprovalEdited"),
          message: t("error.tokenApprovalEdited.message"),
          supportArticleURL: uniswapUrls.helpArticleUrls.approvalsExplainer
        };
      }
      return {
        title: t("error.tokenApproval"),
        message: t("error.access.expiry"),
        supportArticleURL: uniswapUrls.helpArticleUrls.approvalsExplainer
      };
    case TransactionStepType.TokenRevocationTransaction:
      return {
        title: t("common.revoke.approval.failed"),
        message: t("revoke.failed.message"),
        supportArticleURL: uniswapUrls.helpArticleUrls.revokeExplainer
      };
    default:
      return {
        title: t("common.unknownError.error"),
        message: t("common.swap.failed")
      };
  }
}
